import React, { useState, useRef, useEffect } from 'react'
import ApiKeyTable from './ApiKeyTable'
import LegacyAccountKey from './LegacyAccountKey'
import useModal from 'hooks/useModal'
import { Button } from 'semantic-ui-react'
import { toast } from 'libs/toast'
import ApiKeyFormModal from './ApiKeyFormModal'
import ApiKeyCopyModal from './ApiKeyCopyModal'
import ContainerShell from 'shared/components/ContainerShell'
import ContainerShellIntro from 'shared/components/ContainerShellIntro'
import useSaveUserActivityLog, { API_KEY_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import * as R from 'ramda'
import useUser from 'hooks/useUser'

export default function ApiKey({
  apiKeyData,
  legacyApiKey,
  deleteApiKey,
  deleteApiKeyLoading,
  apiKeyFormModal,
  openApiKeyFormModal,
  closeApiKeyFormModal,
  isApiKeyFormModalOpen,
}) {
  const [apiKeys, setApiKeys] = useState([])
  const [selectedDeleteApiKeyId, setSelectedDeleteApiKeyId] = useState(null)
  const [selectedEditApiKeyId, setSelectedEditApiKeyId] = useState(null)
  const [createdApiKey, setCreatedApiKey] = useState(null)
  const saveUserActivityLog = useSaveUserActivityLog()

  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)

  const deleteKeyName = useRef('')
  const {
    Modal: ApiKeyDeleteModal,
    open: openDeleteModal,
    close: closeDeleteModal,
    isOpen: isDeleteModalOpen,
  } = useModal()
  const {
    Modal: CopyModal,
    open: openCopyModal,
    close: closeCopyModal,
    isOpen: isCopyModalOpen,
  } = useModal()

  useEffect(() => {
    setApiKeys(apiKeyData)
  }, [apiKeyData])

  function handleOpenEditModal(apiKeyId) {
    setSelectedEditApiKeyId(apiKeyId)
    openApiKeyFormModal()
  }

  function handleOpenDeleteModal(apiKeyId, apiKeyName) {
    setSelectedDeleteApiKeyId(apiKeyId)
    deleteKeyName.current = apiKeyName
    openDeleteModal()
  }

  function convertApiKeyToLogFormat({
    id,
    name,
    is_full_access,
    key_prefix,
    created_by,
    created_by_fallback,
    scopes,
  }) {
    return {
      id,
      name,
      is_full_access,
      key_prefix,
      created_by,
      created_by_fallback,
      permissions: scopes,
    }
  }

  function handleConfirmDelete() {
    const deletedApiKey = apiKeys.find((apiKey) => apiKey.id === selectedDeleteApiKeyId)
    const logData = convertApiKeyToLogFormat(deletedApiKey)
    deleteApiKey({ url: `account/api-keys/${selectedDeleteApiKeyId}/` })
      .then((response) => {
        setApiKeys((prev) => prev.filter((key) => key.id !== selectedDeleteApiKeyId))
        setSelectedDeleteApiKeyId(null)
        closeDeleteModal()
        toast.success(`API key deleted successfully.`)
        saveUserActivityLog({
          action: API_KEY_ACTION_NAME.deleted_api_key,
          statusCode: response.status,
          requestBody: logData,
          target: `com_${companyId}`,
        })
      })
      .catch((response) => {
        toast.error('Could not delete API key. Contact support for help.')
        saveUserActivityLog({
          action: API_KEY_ACTION_NAME.deleted_api_key,
          statusCode: response.status,
          errorResponseBody: logData,
          target: `com_${companyId}`,
        })
      })
  }

  function handleCreateApiKey(newApiKeyData, fullCreatedApiKey) {
    setApiKeys((prev) => [...prev, newApiKeyData])
    setCreatedApiKey(fullCreatedApiKey)
    openCopyModal()
  }

  function handleUpdateApiKey(updatedApiKey) {
    setApiKeys((prev) =>
      prev.map((apiKey) => (apiKey.id === updatedApiKey.id ? updatedApiKey : apiKey))
    )
    closeApiKeyFormModal()
  }

  const editApiKey = apiKeys.find((apiKey) => apiKey.id === selectedEditApiKeyId) || null

  return (
    <React.Fragment>
      <ContainerShell
        intro={
          <ContainerShellIntro
            helpLinkLabel="Learn how API keys work"
            helpLinkUrl="https://help.simplesat.io/en/articles/4020334"
            title="Your API keys"
          />
        }
      >
        <p>Create API keys to control how external tools access your workspace.</p>
        <ApiKeyTable
          dataSource={apiKeys}
          handleOpenDeleteModal={handleOpenDeleteModal}
          handleOpenAddEditModal={() => openApiKeyFormModal()}
          handleOpenEditModal={handleOpenEditModal}
        />
        <LegacyAccountKey legacyApiKey={legacyApiKey} />
      </ContainerShell>

      {isDeleteModalOpen && (
        <ApiKeyDeleteModal size="tiny">
          <ApiKeyDeleteModal.Header>Delete API key</ApiKeyDeleteModal.Header>
          <ApiKeyDeleteModal.Content>
            <p>
              This will immediately delete <b className="break-all">{deleteKeyName.current}</b>. Any
              applications using this key will no longer be able to access your account. This action
              cannot be undone.
            </p>
          </ApiKeyDeleteModal.Content>
          <ApiKeyDeleteModal.Actions>
            <Button onClick={closeDeleteModal}>Cancel</Button>
            <Button
              negative
              onClick={handleConfirmDelete}
              disabled={deleteApiKeyLoading}
              loading={deleteApiKeyLoading}
            >
              Delete API key
            </Button>
          </ApiKeyDeleteModal.Actions>
        </ApiKeyDeleteModal>
      )}

      {isApiKeyFormModalOpen && (
        <ApiKeyFormModal
          modal={apiKeyFormModal}
          close={closeApiKeyFormModal}
          handleCreateApiKey={handleCreateApiKey}
          handleUpdateApiKey={handleUpdateApiKey}
          selectedApiKey={editApiKey}
          setSelectedEditApiKeyId={setSelectedEditApiKeyId}
          saveUserActivityLog={saveUserActivityLog}
          convertApiKeyToLogFormat={convertApiKeyToLogFormat}
          companyId={companyId}
        />
      )}

      {isCopyModalOpen && createdApiKey && (
        <ApiKeyCopyModal
          modal={CopyModal}
          apiKey={createdApiKey}
          onClose={() => {
            setCreatedApiKey(null)
            closeCopyModal()
          }}
        />
      )}
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/ApiKey/ApiKey.js